import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SwitchTransition, Transition } from 'react-transition-group'

import Button from '@/components/Button/Button'
import IconButton from '@/components/Button/IconButton'
import Pagination from '@/components/Chat/Pagination'
import UIIcon from '@/components/UI/UIIcon'
import { useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import useMedia from '@/utils/hooks/useMedia'

import QuestionsBinaryExplanationsItem from './QuestionsBinaryExplanationsItem'

export type QuestionsExplanationsProps = {
  className?: string
  max: number
  isVisible?: boolean
  questionState?: string
  onChoose?: (choice: string) => void
  scenario: number
}

export default function QuestionsExplanations({
  className,
  max,
  isVisible,
  questionState,
  onChoose,
  scenario,
}: QuestionsExplanationsProps) {
  const { t } = useTranslation()
  const { gsap } = useGSAP()
  const elementRef = useRef<HTMLDivElement>(null)
  const itemRef = useRef<HTMLDivElement>(null)
  const mm = useRef<ReturnType<typeof gsap.matchMedia> | null>(null)
  const isReducedMotion = useMedia('(prefers-reduced-motion: reduce)')

  const { answers, persona } = useAppState()
  const [currentIndex, setIndex] = useState(isVisible ? 1 : 0)
  const [showContinue, setShowContinue] = useState(false)

  useEffect(() => {
    if (showContinue) return

    setShowContinue(currentIndex === max - 1)
  }, [currentIndex, max, showContinue])

  const onNext = useCallback(() => {
    onChoose('explanations')
  }, [onChoose])

  const onEnter = useCallback(() => {
    if (!elementRef.current) return
    mm.current?.revert()
    mm.current = gsap.matchMedia()

    mm.current.add('(prefers-reduced-motion: reduce)', () => {
      gsap.set(elementRef.current, { opacity: 1 })
      return () => {
        // no animation to clean up for reduced motion
      }
    })

    mm.current.add('(prefers-reduced-motion: no-preference)', () => {
      const tl = gsap
        .timeline({ delay: 0.4 })
        .fromTo(elementRef.current, { opacity: 0 }, { opacity: 1, duration: 0.5, ease: 'power2.out' }, 0)
      return () => {
        tl.kill()
      }
    })
  }, [gsap, elementRef])

  const hasRightAnswer = useMemo(() => {
    return (
      answers.after[`after_${currentIndex + 1}`] ===
      t(`questions.${persona}.${scenario}.${questionState}.explanation.${currentIndex + 1}.isTrue`)
    )
  }, [answers, persona, questionState, currentIndex, t, scenario])

  return (
    <Transition
      nodeRef={elementRef}
      in={isVisible}
      onEnter={onEnter}
      // onExit={onExit}
      timeout={{
        enter: 900,
        exit: 150,
      }}
      mountOnEnter
      unmountOnExit
    >
      <div
        ref={elementRef}
        className='QuestionsExplanations fixed inset-0 size-full flex flex-col items-center justify-center bg-cream'
      >
        <div className='relative size-full flex items-center justify-center'>
          <div className='self-stretch relative w-full portrait:max-w-cols-12 md:max-w-cols-6 flex items-center justify-center px-12'>
            {isReducedMotion ? (
              <div className='absolute inset-0 size-full'>
                <UIIcon icon='background1' className='absolute size-full text-dark-cream' />
              </div>
            ) : (
              <div className='absolute inset-0 size-full transition-transform duration-800 ease-out-quad'>
                <UIIcon
                  icon='background1'
                  className={`absolute size-full text-dark-cream transition-transform duration-1000 ease-in-out-quad ${
                    hasRightAnswer ? 'scale-100' : 'scale-0'
                  }`}
                />
                <UIIcon
                  icon='background2'
                  className={`absolute size-full text-dark-cream transition-transform duration-1000 ease-in-out-quad ${
                    hasRightAnswer ? 'scale-0' : 'scale-100'
                  }`}
                />
              </div>
            )}

            <div className='relative my-auto z-2 w-full portrait:max-w-cols-12 md:max-w-cols-4 flex flex-col items-center justify-center gap-12'>
              <SwitchTransition>
                <Transition
                  key={`explanation_item_${currentIndex}`}
                  appear
                  mountOnEnter
                  unmountOnExit
                  nodeRef={itemRef}
                  timeout={{
                    enter: 500,
                    exit: 300,
                  }}
                >
                  {(state) => (
                    <QuestionsBinaryExplanationsItem
                      ref={itemRef}
                      isLeaving={state === 'exiting'}
                      question={`questions.${persona}.${scenario}.${questionState}.binary.${currentIndex + 1}`}
                      answer={answers.after[`after_${currentIndex + 1}`]}
                      goodAnswer={t(
                        `questions.${persona}.${scenario}.${questionState}.explanation.${currentIndex + 1}.isTrue`
                      )}
                      explanation={`questions.${persona}.${scenario}.${questionState}.explanation.${currentIndex + 1}.text`}
                    />
                  )}
                </Transition>
              </SwitchTransition>
              <div className='hidden md:flex items-center justify-start absolute top-0 right-full h-full w-cols-2'>
                {currentIndex > 0 && (
                  <IconButton
                    label={t('questions.aria.explanation.previous')}
                    icon='previous'
                    disabled={currentIndex === 0 ? true : false}
                    onClick={() => setIndex(gsap.utils.wrap(0, max, currentIndex - 1))}
                  />
                )}
              </div>
              <div className='hidden md:flex items-center justify-end absolute top-0 left-full h-full w-cols-2'>
                {currentIndex < max - 1 && (
                  <IconButton
                    label={t('questions.aria.explanation.next')}
                    icon='next'
                    labelPosition='left'
                    disabled={currentIndex === max - 1 ? true : false}
                    onClick={() => setIndex(gsap.utils.wrap(0, max, currentIndex + 1))}
                  />
                )}
              </div>
            </div>

            <div className='absolute bottom-90 flex items-center justify-center w-full gap-40'>
              <IconButton
                className={`md:hidden ${currentIndex === 0 ? 'opacity-0' : ''}`}
                label={t('questions.aria.explanation.previous')}
                icon='previous'
                disabled={currentIndex === 0 ? true : false}
                onClick={() => setIndex(gsap.utils.wrap(0, max, currentIndex - 1))}
              />

              <Pagination current={currentIndex} max={max} />

              <IconButton
                className={`md:hidden ${currentIndex === max - 1 ? 'opacity-0' : ''}`}
                label={t('questions.aria.explanation.next')}
                icon='next'
                labelPosition='left'
                disabled={currentIndex === max - 1 ? true : false}
                onClick={() => setIndex(gsap.utils.wrap(0, max, currentIndex + 1))}
              />
            </div>
          </div>

          {showContinue && (
            <div className='absolute inset-x-12 bottom-24 flex flex-col items-center justify-center pointer-events-none'>
              <Button className='pointer-events-auto' appear={true} appearDelay={0.8} onClick={onNext}>
                {t('questions.explanation.continue')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Transition>
  )
}
